import { Resources } from 'constants/resources';

import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { ApiClientsDeleteDialog } from 'pages/ApiClients/components/ApiClientsDeleteDialog/ApiClientsDeleteDialog';
import {
  HttpError,
  Identifier,
  SimpleForm,
  useEditController,
  useNotify,
  useUpdate,
} from 'react-admin';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { DeleteButton, CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { ApiClient } from 'types/embedded/apiClient';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils/getId';
import { getSubmissionErrors } from 'utils/getSubmissionErrors';

import { ApiClientEditFormValues, ApiClientsEditForm } from './ApiClientsEditForm';

type ApiClientEditPayload = Omit<ApiClientEditFormValues, 'id' | 'merchant'> & {
  id: Identifier;
  merchant: string | null;
};

const transform = (data: ApiClientEditFormValues): ApiClientEditPayload => {
  return {
    ...data,
    id: parseInt(getId(data.id), 10),
    merchant: data.merchant || null,
  };
};

export const ApiClientsEdit = () => {
  const { hasResourcePermissions } = usePermissions();
  const navigate = useNavigate();
  const notify = useNotify();
  const { toRoot } = useNavigateToRoot();
  const { record } = useEditController<ApiClient>();
  const [update] = useUpdate<ApiClientEditPayload>();
  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<ApiClient>();

  async function onSubmit(values: ApiClientEditFormValues) {
    if (!record) {
      return;
    }

    try {
      const data = await update(
        Resources.ApiClients,
        { id: record.id, data: transform(values) },
        { returnPromise: true },
      );
      notify(`Клиент API ${data?.name} изменен`, { type: 'success' });
      toRoot();
    } catch (error) {
      notify(`Ошибка: ${(error as Error).message || 'ошибка сервера'}`, {
        type: 'error',
        autoHideDuration: 3000,
      });
      return getSubmissionErrors(error as HttpError);
    }
  }

  return (
    <EditPage listName="Клиенты API" mutationMode="pessimistic" title="Редактирование Клиента API">
      <SimpleForm
        onSubmit={onSubmit as SubmitHandler<FieldValues>}
        sanitizeEmptyValues
        sx={{ '&.MuiCardContent-root': { padding: 0 } }}
        toolbar={false}
      >
        <ApiClientsEditForm />
        <ApiClientsDeleteDialog
          onClose={handleCloseRecordModal}
          onSuccess={() => {
            navigate(-1);
            notify(`Клиент API ${record?.name} удален`, {
              type: 'success',
            });
          }}
          open={openRecordModal}
          record={record}
        />
        <CardActionsButtons
          leftActionsSlot={
            hasResourcePermissions(Resources.ApiClients, [Permission.Full, Permission.Delete]) ? (
              <DeleteButton onClick={() => handleOpenRecordModal(record)} variant="outlined" />
            ) : undefined
          }
          rightActionsSlot={[
            <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
            <SaveButton key="save-button" />,
          ]}
          sx={{ marginTop: 'auto', padding: 0 }}
        />
      </SimpleForm>
    </EditPage>
  );
};
